/**
 * Окно микрофона. Два вида:
 *   объяснение перед первым системным запросом — «Разрешить» открывает
 *   микрофон прямо в касании (`allowFromModal`), как требует iOS;
 *   отказ — точная причина (запрет, нет HTTPS, микрофон занят…), шаги для
 *   этого телефона и «Попробовать снова», который снова зовёт
 *   `getUserMedia` внутри жеста. На текст — только по выбору ученика.
 */

import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { micEnvironment, micPlatform } from '../services/micDiagnostics';
import { allowFromModal, declineFromModal, retryable, useMicPromptStore } from '../services/micPermission';
import { Button } from './Button';
import { IconMicrophone, IconMicrophoneOff } from './icons';

function httpsUrl(): string | null {
  if (typeof window === 'undefined' || window.location.protocol !== 'http:') return null;
  return `https://${window.location.host}${window.location.pathname}${window.location.search}${window.location.hash}`;
}

export function MicPermissionModal() {
  const { t } = useTranslation();
  const modal = useMicPromptStore((state) => state.modal);
  if (modal === null) return null;

  const blocked = modal.mode === 'blocked' ? modal : null;
  const kind = blocked?.failure.kind;
  const platform = micPlatform();
  const inApp = blocked !== null && kind === 'unsupported' ? micEnvironment().inApp : null;
  const steps =
    kind === 'denied'
      ? t(`mic.steps.denied.${platform}`)
          .split('\n')
          .filter((line) => line.trim().length > 0)
      : [];
  const https = kind === 'insecure' ? httpsUrl() : null;

  return createPortal(
    <div role="dialog" aria-modal="true" aria-labelledby="mic-permission-title" className="fixed inset-0 z-[2147483600] grid place-items-center bg-heading/40 p-4 backdrop-blur-sm">
      <div className="glass flex max-h-[90dvh] w-full max-w-sm flex-col items-center gap-4 overflow-y-auto rounded-card border border-line bg-surface-solid p-6 text-center shadow-card-lg">
        <span className={`grid h-14 w-14 shrink-0 place-items-center rounded-full ${blocked === null ? 'bg-accent/20 text-accent-dark' : 'bg-danger/15 text-danger'}`}>
          {blocked === null ? <IconMicrophone size={28} aria-hidden="true" /> : <IconMicrophoneOff size={28} aria-hidden="true" />}
        </span>
        <h2 id="mic-permission-title" className="font-heading text-xl font-extrabold text-heading">
          {blocked === null ? t('call.micPromptTitle') : t(`mic.title.${kind ?? 'other'}`)}
        </h2>

        {blocked === null && <p className="text-muted">{t('call.micPromptBody')}</p>}

        {blocked !== null && steps.length > 0 && (
          <ol className="flex w-full list-decimal flex-col gap-1 pl-5 text-left text-sm text-heading">
            {steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        )}
        {blocked !== null && steps.length === 0 && <p className="text-muted">{t(`mic.body.${kind === undefined || kind === 'denied' ? 'other' : kind}`, { origin: typeof window === 'undefined' ? '' : window.location.origin })}</p>}
        {inApp !== null && <p className="text-sm text-heading">{t('mic.inApp', { app: inApp })}</p>}
        {blocked !== null && blocked.attempts > 0 && !blocked.retrying && <p className="text-sm font-bold text-danger">{t('mic.stillBlocked')}</p>}
        {blocked !== null && (
          // Точная причина — для поддержки и для отладки на телефоне.
          <p className="break-all font-mono text-xs text-muted" data-testid="mic-error">
            {blocked.failure.name}
            {blocked.failure.message.length > 0 && blocked.failure.message !== blocked.failure.name ? `: ${blocked.failure.message}` : ''}
          </p>
        )}

        <div className="flex w-full flex-col gap-2">
          {(blocked === null || retryable(blocked.failure)) && (
            <Button onClick={allowFromModal} disabled={blocked?.retrying === true}>
              {blocked === null ? t('call.micPromptAllow') : blocked.retrying ? t('mic.retrying') : t('mic.retry')}
            </Button>
          )}
          {https !== null && (
            <Button onClick={() => window.location.assign(https)}>{t('mic.openHttps')}</Button>
          )}
          {blocked !== null && blocked.attempts > 0 && !blocked.retrying && (
            <Button variant="secondary" onClick={() => window.location.reload()}>
              {t('mic.reload')}
            </Button>
          )}
          <button type="button" onClick={declineFromModal} className="py-2 text-sm font-bold text-muted hover:text-heading">
            {t('call.micPromptText')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
